import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@mui/material/Grid';
import { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Box from '@mui/system/Box';
import { Waypoint_Update_Destroyable } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm, useWatch } from 'react-hook-form';

import AutocompleteMaterialsTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteMaterialsTypeahead';
import DateInput from '~components/FormFields/v2/DateInput';
import QuantityAndUnitInput from '~components/FormFields/v2/QuantityAndUnitInput';
import TextInput from '~components/FormFields/v2/TextInput';
import TimeInput from '~components/FormFields/v2/TimeInput';
import type { PhaseOptions } from '~components/Order/PhaseDrawer/types';
import { PhaseSelection } from '~components/Order/PhaseSelection';
import { SiteSelection } from '~components/Order/SiteSelection/SiteSelection';
import { FeatureFlags } from '~constants/featureFlags';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { Job } from '~hooks/useJob/models';
import { Load } from '~hooks/useLoad/models';
import { Order } from '~hooks/useOrders/models';
import { Site, SiteBasic } from '~hooks/useSites/models';
import { Ticket } from '~hooks/useTickets/models';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import {
  getScaleTicketEditableFields,
  getScaleTicketRows,
} from '~utils/tickets/scale-ticket-fields';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { FlexRow } from '../styled';
import { getAnchorStampDate } from './stampDateLogic';
import StampDateTimeField from './StampDateTimeField';
import { STAMP_FIELDS, StampFieldError } from './stampFields';
import {
  getTicketSchema,
  setTicketSchemaDefaultValues,
  TICKET_NUMBER_MAX_LENGTH,
  TicketDto,
} from './ticketSchema';
import { buildLoadWaypointsUpdate } from './waypointsUpdate';

interface TicketFormProps {
  job?: Job;
  load?: Load;
  order?: Order;
  sx?: SxProps<Theme>;
  ticket?: Ticket;
}

export type TicketFormHandler = {
  onSubmit: () => Promise<TicketDto>;
  applyStampFieldErrors: (stampFieldErrors: StampFieldError[]) => void;
  getIsDirty: () => boolean;
  getPhaseId: () => string | null | undefined;
  getWaypointsUpdate: () => Waypoint_Update_Destroyable[] | undefined;
};

const TicketForm = forwardRef<TicketFormHandler, TicketFormProps>(function TicketForm(
  { job, load, order, sx, ticket },
  ref,
) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const ticketNumberOptional = useFeatureFlag({
    featureFlagKey: FeatureFlags.ticketNumberOptionalOnWeb,
  });

  const schema = useMemo(
    () => getTicketSchema(!ticketNumberOptional),
    [ticketNumberOptional],
  );

  const phaseIdRef = useRef<string | null | undefined>(undefined);

  const phaseOptions: PhaseOptions[] = useMemo(() => {
    return (
      order?.phases?.map((phase) => ({
        id: phase.id,
        name: phase.name,
        code: phase.code,
      })) ?? []
    );
  }, [order?.phases]);

  const selectedPhase: PhaseOptions | null = useMemo(() => {
    const phase = load?.phase;
    if (!phase) return null;
    return { id: phase.id, name: phase.name, code: phase.code };
  }, [load?.phase]);

  const handlePhaseChange = useCallback((option: PhaseOptions | null) => {
    phaseIdRef.current = option?.id ?? null;
  }, []);

  // Compare against the load's current phase so the phase only counts as changed
  // when the user actually picks a different one (mirrors the site dirty checks).
  const initialPhaseId = load?.phase?.id ?? null;

  const initialPickUpSite = load?.pickUpWaypoints?.[0]?.site ?? null;
  const initialDropOffSite = load?.dropOffWaypoints?.[0]?.site ?? null;
  const initialStagingSite = load?.stagingWaypoints?.[0]?.site ?? null;

  const ticketCompanyId = job?.company?.id;
  const userOwnsTicketCompany = !!ticketCompanyId && ticketCompanyId === companyId;

  const [pickUpSite, setPickUpSite] =
    useState<Nullable<Site | SiteBasic>>(initialPickUpSite);
  const [dropOffSite, setDropOffSite] =
    useState<Nullable<Site | SiteBasic>>(initialDropOffSite);
  const [stagingSite, setStagingSite] =
    useState<Nullable<Site | SiteBasic>>(initialStagingSite);
  const [pickUpDirty, setPickUpDirty] = useState(false);
  const [dropOffDirty, setDropOffDirty] = useState(false);
  const [stagingDirty, setStagingDirty] = useState(false);

  // SiteSelection fires its onChange callbacks during initial mount with the
  // pre-populated values. Compare against the initial site id so we only flag
  // a waypoint as dirty when the user actually picks a different site.
  const initialPickUpSiteId = initialPickUpSite?.id ?? null;
  const initialDropOffSiteId = initialDropOffSite?.id ?? null;
  const initialStagingSiteId = initialStagingSite?.id ?? null;

  const handlePickUpSiteChange = useCallback(
    (site?: Site) => {
      setPickUpSite(site ?? null);
      setPickUpDirty((site?.id ?? null) !== initialPickUpSiteId);
    },
    [initialPickUpSiteId],
  );
  const handleDropOffSiteChange = useCallback(
    (site?: Site) => {
      setDropOffSite(site ?? null);
      setDropOffDirty((site?.id ?? null) !== initialDropOffSiteId);
    },
    [initialDropOffSiteId],
  );
  const handleStagingSiteChange = useCallback(
    (site?: Site) => {
      setStagingSite(site ?? null);
      setStagingDirty((site?.id ?? null) !== initialStagingSiteId);
    },
    [initialStagingSiteId],
  );

  const form = useForm<TicketDto>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setTicketSchemaDefaultValues(ticket, load, job),
  });

  const { clearErrors, getFieldState, getValues, setValue, watch } = form;

  // Read isDirty during render so react-hook-form keeps it live; the imperative
  // getIsDirty() below closes over this value.
  const { isDirty: formIsDirty } = form.formState;

  const [arrivedPickupTime, loadedTime, arrivedDropoffTime, unloadedTime] = useWatch({
    control: form.control,
    name: ['arrivedPickupTime', 'loadedTime', 'arrivedDropoffTime', 'unloadedTime'],
  });

  // A stamp that just received its first time has no date yet — default it to the
  // anchor stamp's date (falling back to the ticket date) so a full datetime can
  // be assembled at save time and the caption renders immediately.
  useEffect(() => {
    STAMP_FIELDS.forEach(({ dateName, isReadOnly, timeName }) => {
      if (isReadOnly) {
        return;
      }

      const time = getValues(timeName);
      const date = getValues(dateName);

      if (time && !date) {
        const anchorStamps = STAMP_FIELDS.map((stampField) => ({
          date: getValues(stampField.dateName) ?? null,
          time: getValues(stampField.timeName) ?? null,
        }));
        const anchorDate = getAnchorStampDate(anchorStamps);

        setValue(dateName, (anchorDate ?? getValues('date')) as unknown as Date);
      }
    });
  }, [
    arrivedPickupTime,
    loadedTime,
    arrivedDropoffTime,
    unloadedTime,
    getValues,
    setValue,
  ]);

  // a server-side stamp validation error (TRE-15021) describes the relationship
  // between two stamps but renders under only one, and editing either side can fix
  // it — including the paired date field. any stamp edit therefore invalidates the
  // applied server errors: clear them and let the next save re-validate against the
  // backend. yup errors keep type 'required'/'typeError', so only server-applied
  // ('manual') errors are cleared.
  useEffect(() => {
    const subscription = watch((_formValues, { name: changedFieldName }) => {
      const changedFieldIsStampField = STAMP_FIELDS.some(
        ({ dateName, timeName }) =>
          changedFieldName === dateName || changedFieldName === timeName,
      );

      if (!changedFieldIsStampField) {
        return;
      }

      STAMP_FIELDS.forEach(({ timeName }) => {
        if (getFieldState(timeName).error?.type === 'manual') {
          clearErrors(timeName);
        }
      });
    });

    return () => subscription.unsubscribe();
  }, [clearErrors, getFieldState, watch]);

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          form.handleSubmit((data) => {
            resolve(data);
          })();
        });
      },
      applyStampFieldErrors(stampFieldErrors: StampFieldError[]) {
        stampFieldErrors.forEach(({ field, message }) => {
          form.setError(field, { type: 'manual', message });
        });
      },
      getIsDirty() {
        const phaseChanged =
          phaseIdRef.current !== undefined && phaseIdRef.current !== initialPhaseId;

        return formIsDirty || pickUpDirty || dropOffDirty || stagingDirty || phaseChanged;
      },
      getPhaseId() {
        return phaseIdRef.current;
      },
      getWaypointsUpdate() {
        return buildLoadWaypointsUpdate(
          load?.waypoints ?? [],
          { pickUp: pickUpSite, dropOff: dropOffSite, staging: stagingSite },
          { pickUp: pickUpDirty, dropOff: dropOffDirty, staging: stagingDirty },
        );
      },
    }),
    [
      form.handleSubmit,
      form.setError,
      formIsDirty,
      initialPhaseId,
      load?.waypoints,
      pickUpSite,
      dropOffSite,
      stagingSite,
      pickUpDirty,
      dropOffDirty,
      stagingDirty,
    ],
  );

  return (
    <Box sx={[...(Array.isArray(sx) ? sx : [sx])]}>
      <FlexRow
        sx={{
          alignItems: 'center',
          height: `${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px`,
        }}
      >
        <Typography
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: '18px',
            fontWeight: 600,
          })}
        >
          {t('ticket_drawer_v2.details.title')}
        </Typography>
      </FlexRow>

      <Box
        sx={{
          columnGap: 2,
          display: 'grid',
          gridAutoColumns: '1fr',
          mt: 2,
          rowGap: 3,
        }}
      >
        <Box sx={{ gridColumn: 'span 2' }}>
          <TextInput
            control={form.control}
            isRequired={!ticketNumberOptional}
            label={t('approvals.ticket_details.ticket_number')}
            name="ticketNumber"
            slotProps={{ htmlInput: { maxLength: TICKET_NUMBER_MAX_LENGTH } }}
          />
        </Box>

        <Box>
          <TimeInput
            control={form.control}
            isRequired
            label={t('loads.ticket_time')}
            name="time"
          />
        </Box>

        <Box>
          <DateInput
            control={form.control}
            isRequired
            label={t('approvals.ticket_details.ticket_date')}
            name="date"
          />
        </Box>

        <Box>
          <QuantityAndUnitInput
            control={form.control}
            isRequired
            label={t('approvals.ticket_details.quantity')}
            name="quantityAndUnit"
          />
        </Box>

        <Box>
          <AutocompleteMaterialsTypeahead
            companyId={ticketCompanyId || companyId}
            orderId={job?.order?.id}
            control={form.control}
            isRequired
            label={t('approvals.ticket_details.material')}
            name="material"
          />
        </Box>

        <Grid container spacing={2} sx={{ gridColumn: 'span 2' }}>
          <PhaseSelection
            canEdit={false}
            canAdd={false}
            isDisabled={!order}
            onChange={handlePhaseChange}
            phaseOptions={phaseOptions}
            selectedPhase={selectedPhase}
            disableAutoDefaultedPhase
          />
        </Grid>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Typography
          sx={(theme) => ({
            color: theme.brandV2.colors.treadBlack,
            fontSize: '18px',
            fontWeight: 600,
            mb: 2,
          })}
        >
          {t('ticket_drawer_v2.digital_ticket.sites')}
        </Typography>

        <SiteSelection
          allowSuggestions={false}
          companyId={job?.company?.id}
          disableDropOffEditCta={!userOwnsTicketCompany}
          disablePickUpEditCta={!userOwnsTicketCompany}
          disableStagingEditCta={!userOwnsTicketCompany}
          dropOffSite={initialDropOffSite}
          hideAddDefaultSiteAction
          ignoreCompanyNotFound={!userOwnsTicketCompany}
          isRequired={false}
          onDropOffSiteChange={handleDropOffSiteChange}
          onPickUpSiteChange={handlePickUpSiteChange}
          onStagingSiteChange={handleStagingSiteChange}
          orderId={job?.order?.id}
          pickUpSite={initialPickUpSite}
          projectId={job?.project?.id}
          showMap={false}
          stagingSite={initialStagingSite}
        />
      </Box>

      {load?.loadSummary && (
        <Box sx={{ mt: 4 }}>
          <Typography
            sx={(theme) => ({
              color: theme.brandV2.colors.treadBlack,
              fontSize: '18px',
              fontWeight: 600,
              mt: 3,
            })}
          >
            {t('loads.times')}
          </Typography>

          <Box
            sx={{
              columnGap: 2,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              mt: 2,
              rowGap: 3,
            }}
          >
            {STAMP_FIELDS.map((stampField) => (
              <StampDateTimeField
                control={form.control}
                dateName={stampField.dateName}
                isReadOnly={stampField.isReadOnly}
                key={stampField.timeName}
                label={t(stampField.labelKey)}
                timeName={stampField.timeName}
              />
            ))}
          </Box>
        </Box>
      )}

      {job?.vetaCenterBound && (
        <Box sx={{ mt: 4 }}>
          <Typography
            component="h3"
            sx={(theme) => ({
              color: theme.brandV2.colors.treadBlack,
              fontSize: '18px',
              fontWeight: 600,
            })}
          >
            {t('approvals.ticket_details.scale_ticket.title')}
          </Typography>

          <Box
            component="dl"
            sx={{
              columnGap: 2,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              m: 0,
              mt: 2,
              rowGap: 3,
            }}
          >
            {getScaleTicketRows(ticket).map(({ key, label, value }) => (
              <Box key={key}>
                <Typography
                  component="dt"
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                    fontSize: '12px',
                  })}
                >
                  {label}
                </Typography>
                <Typography component="dd" sx={{ m: 0, minHeight: '24px' }}>
                  {value}
                </Typography>
              </Box>
            ))}

            {getScaleTicketEditableFields(ticket).map(({ label, name }) => (
              <TextInput control={form.control} key={name} label={label} name={name} />
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        <TextInput
          rows={3}
          control={form.control}
          label={t('approvals.ticket_details.notes')}
          name="loadsNotes"
        />
      </Box>
    </Box>
  );
});

export default TicketForm;
